import React, { useState } from 'react';
import { submitBrochureRequest } from '../services/firebaseService';
import { downloadCatalogFile } from '../utils/catalogUtils';

const BrochureLeadForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleClose = () => {
    setShowThankYou(false);
    setFormData({
      companyName: '',
      contactPerson: '',
      email: '',
      phone: '',
      message: ''
    });
    onClose();
  };

  const handleDownload = async () => {
    const success = await downloadCatalogFile();
    if (!success) {
      alert('Sorry, the catalog is currently unavailable. Please try again later.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitBrochureRequest(formData);
      
      if (result.success) {
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          message: ''
        });
        setShowThankYou(true);
      } else {
        alert('There was an error submitting your request. Please try again.');
        console.error('Brochure form error:', result.error);
      }
    } catch (error) {
      alert('There was an error submitting your request. Please try again.');
      console.error('Brochure form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-burlap-800 font-display">
                {showThankYou ? 'Thank You!' : 'Download Gift Brochure'}
              </h3>
              <p className="text-burlap-600 text-sm mt-1">
                {showThankYou ? 'Your request has been submitted successfully' : 'Get our complete catalog with pricing'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {showThankYou ? (
            /* Thank You Message with Download */
            <div className="text-center space-y-6">
              <div className="text-6xl">🎉</div>
              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-burlap-800">
                  Request Submitted Successfully!
                </h4>
                <p className="text-burlap-600">
                  Thank you for your interest in our gift collections. Your catalog download is ready!
                </p>
              </div>
              
              <button
                onClick={handleDownload}
                className="w-full py-4 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 transform hover:scale-[1.02] shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-3"
              >
                <span>📄</span>
                <span>Download Diwali Gifts Catalog</span>
                <span>⬇️</span>
              </button>

              <div className="bg-burlap-50 rounded-lg p-4">
                <p className="text-sm text-burlap-700">
                  <strong>What's Next?</strong><br/>
                  Our team will contact you within 24 hours to discuss your requirements and provide personalized assistance.
                </p>
              </div>

              <button
                onClick={handleClose}
                className="text-burlap-600 hover:text-burlap-800 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            /* Lead Form */
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
                    placeholder="Company Name *"
                  />
                </div>

                <div>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
                    placeholder="Contact Person Name *"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
                    placeholder="Official Email ID *"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
                    placeholder="Phone Number *"
                  />
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 resize-none placeholder-gray-400"
                    placeholder="Message / Requirement (optional)"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 px-6 rounded-xl font-semibold text-white transition-all duration-300 ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 transform hover:scale-[1.02] shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Download Brochure'}
                </button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                We respect your privacy and will only use your information to send you our brochure.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrochureLeadForm;
